
import { defineComponent, onMounted, reactive, toRefs } from "vue";
import { fetchAlertHistory, fetchSensorTagOptions, fetchThresholds } from "@/helpers/api";
import { formatTime, formatDuration, timeNowAsJS, getNextDayFromDate, jsTimeToRealEpoch } from "@/helpers/time";
import { getDateFormat } from "@/helpers/store";
import { AlertHistoryResponse, LocalThreshold, toLocalThreshold } from "@/interfaces/Threshold";
import { diagnosisLink } from "@/helpers/helpers";
import VueFeather from "vue-feather";
import Datepicker from "vue3-datepicker";
import PrettifyJSON from "@/components/PrettifyJSON.vue";
import ThresholdConfigModal from "@/components/ThresholdConfigModal.vue";
import { PermissionsError } from "@/errors/errors";
import { ref } from "vue";

export default defineComponent({
  name: "AlertsPage",
  components: {
    ThresholdConfigModal,
    VueFeather,
    PrettifyJSON,
    Datepicker,
  },
  setup() {
    const state = reactive({
      thresholds: [] as LocalThreshold[],
      sensor_tag_options: {} as Record<string, string[]>, // list of '{"k": "v"}' JSON strings
      thresholds_loading: true as boolean,
      selected_threshold: null as LocalThreshold | null,
      errorMsg: "" as string,
      pickerStartDate: timeNowAsJS(-7) as Date,
      pickerEndDate: timeNowAsJS() as Date,
      dateFormat: getDateFormat(),
    });

    const getThresholds = async () => {
      try {
        const data = await fetchThresholds();
        state.thresholds = data.map((threshold) => toLocalThreshold(threshold));
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage thresholds";
        }
      }
      state.thresholds_loading = false;
    };

    const showNewThreshold = () => {
      state.selected_threshold = {
        ThresholdID: null,
        Name: "New threshold",
        Description: null,
        Selector: {
          sensor_name_like: undefined,
          tags_like: {},
          tags_in: {},
        },
        Value: null,
        Severity: "warning",
        CreatedAt: null,
        Enabled: true,
        DashboardUID: null,
        Managed: false,
      };
    };

    const showConfigureThreshold = (threshold: LocalThreshold) => {
      state.selected_threshold = threshold;
    };

    const addSavedThreshold = (threshold: LocalThreshold) => {
      const idx = state.thresholds.findIndex((t) => t.ThresholdID === threshold.ThresholdID);
      if (idx >= 0) {
        state.thresholds[idx] = threshold;
      } else {
        state.thresholds.push(threshold);
      }
    };

    const handleDeletedThreshold = (thresholdID: number) => {
      const idx = state.thresholds.findIndex((t) => t.ThresholdID === thresholdID);
      if (idx >= 0) {
        state.thresholds.splice(idx, 1);
      }
    };

    const onClearSelectedThreshold = () => {
      state.selected_threshold = null;
    };

    getThresholds();

    onMounted(async () => {
      try {
        state.sensor_tag_options = await fetchSensorTagOptions();
      } catch (error) {
        if (error instanceof PermissionsError) {
          state.errorMsg = "You do not have permission to manage tasks";
        }
      }
    });

    const expandedDescriptions = ref(new Set());

    const toggleDescription = (id: number) => {
      const next = new Set(expandedDescriptions.value);
      next.has(id) ? next.delete(id) : next.add(id);
      expandedDescriptions.value = next;
    };

    const isLongDescription = (desc: string) => {
      return desc && desc.length > 120;
    };

    const expandedHistoryRows = ref(new Set<number>());
    const alertHistoryCache = ref<Record<number, AlertHistoryResponse>>({});
    const alertHistoryLoading = ref(new Set<number>());

    const getDateEpochs = () => {
      const fromEpoch = Math.floor(state.pickerStartDate.getTime() / 1000);
      const toEpoch = Math.floor(state.pickerEndDate.getTime() / 1000);
      return { fromEpoch, toEpoch };
    };

    const fetchHistoryForThreshold = async (thresholdId: number) => {
      const loading = new Set(alertHistoryLoading.value);
      loading.add(thresholdId);
      alertHistoryLoading.value = loading;

      const { fromEpoch, toEpoch } = getDateEpochs();
      try {
        const data = await fetchAlertHistory(thresholdId, fromEpoch, toEpoch);
        alertHistoryCache.value = {
          ...alertHistoryCache.value,
          [thresholdId]: data,
        };
      } catch (error) {
        alertHistoryCache.value = {
          ...alertHistoryCache.value,
          [thresholdId]: {
            thresholdId,
            from: 0,
            to: 0,
            history: [],
            grafanaError: error instanceof Error ? error.message : "Failed to fetch alert history",
          },
        };
      } finally {
        const done = new Set(alertHistoryLoading.value);
        done.delete(thresholdId);
        alertHistoryLoading.value = done;
      }
    };

    const toggleAlertHistory = async (thresholdId: number) => {
      const next = new Set(expandedHistoryRows.value);
      if (next.has(thresholdId)) {
        next.delete(thresholdId);
        expandedHistoryRows.value = next;
        return;
      }
      next.add(thresholdId);
      expandedHistoryRows.value = next;

      if (alertHistoryCache.value[thresholdId]) {
        return;
      }

      await fetchHistoryForThreshold(thresholdId);
    };

    const pushEndDate = () => {
      if (state.pickerStartDate > state.pickerEndDate) {
        state.pickerEndDate = getNextDayFromDate(state.pickerStartDate);
      }
    };

    const refreshAlertHistory = async () => {
      pushEndDate();
      alertHistoryCache.value = {};
      const expanded = Array.from(expandedHistoryRows.value);
      await Promise.all(expanded.map((id) => fetchHistoryForThreshold(id)));
    };

    return {
      ...toRefs(state),
      getThresholds,
      showNewThreshold,
      diagnosisLink,
      showConfigureThreshold,
      addSavedThreshold,
      handleDeletedThreshold,
      onClearSelectedThreshold,
      formatTime,
      expandedDescriptions,
      toggleDescription,
      isLongDescription,
      expandedHistoryRows,
      alertHistoryCache,
      alertHistoryLoading,
      toggleAlertHistory,
      pushEndDate,
      refreshAlertHistory,
      formatDuration,
    };
  },
});
